import { useEffect, useMemo, useState } from "react";
import '../css/prog.css';

export default function ProgressWithStatuses({ onComplete, timer }) {
    const MESSAGES = useMemo(
        () => [
            "Confirming the details you submitted…",
            "Running debt relief savings engine…",
            "Checking program eligibility…",
            "Comparing multiple relief options…",
            "Identifying your lowest potential payment…",
            "Finalizing your personalized savings report…"
        ],
        []
    );

    const DURATION_MS = timer ?? 15_000;
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        const start = Date.now();

        const tick = () => {
            const elapsed = Date.now() - start;
            const pct = Math.min(100, Math.round((elapsed / DURATION_MS) * 100));
            setProgress(pct);

            if (elapsed >= DURATION_MS) {
                clearInterval(timer);
                onComplete();
            }
        };

        tick(); // initial
        const timer = setInterval(tick, 100); // update every 100ms
        return () => clearInterval(timer);
        // eslint-disable-next-line
    }, []);

    const size = 200;
    const strokeWidth = 12;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const dashOffset = circumference - (progress / 100) * circumference;

    const progressColor = progress < 50 ? '#d32f2f' : progress < 80 ? '#f0a000' : '#1c8300';

    const STATUS_COMPLETION_RATIO = 13 / 15;
    const statusProgressCap = STATUS_COMPLETION_RATIO * 100;
    const statusProgress = Math.min(100, (progress / statusProgressCap) * 100);

    const statusStates = MESSAGES.map((message, index) => {
        const total = MESSAGES.length;
        const chunk = 100 / total;
        const startThreshold = index * chunk;
        const endThreshold = (index + 1) * chunk;

        let state = 'pending';
        if (statusProgress >= endThreshold) state = 'done';
        else if (statusProgress >= startThreshold) state = 'active';

        return { message, state };
    });

    return (
        <div className="progress-wrap">
            <div className="progress-circle" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
                <svg width={size} height={size}>
                    <circle
                        className="progress-circle__bg"
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        strokeWidth={strokeWidth}
                    />
                    <circle
                        className="progress-circle__indicator"
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        strokeWidth={strokeWidth}
                        stroke={progressColor}
                        strokeDasharray={circumference}
                        strokeDashoffset={dashOffset}
                    />
                </svg>
                <div className="progress-circle__label">
                    <span>{progress}%</span>
                </div>
            </div>
            <div className="progress-status-list">
                {statusStates.map(({ message, state }, idx) => (
                    <div className="progress-status-item" key={idx}>
                        <div className={`mini-progress ${state}`}>
                            {state === 'done' ? (
                                <span className="mini-progress__check">✓</span>
                            ) : (
                                <span className="mini-progress__spinner" />
                            )}
                        </div>
                        <span className="progress-status-text">{message}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}
